import React from 'react';
import { InvoiceStatus } from '../../types';
import { InvoiceStats, monthComparisonSub, monthLabel } from '../../services/invoiceStats';
import { formatCents } from '../../services/money';
import {
    Euro,
    AlertTriangle,
    PenLine,
    ClipboardCheck,
    Wallet,
    CalendarClock,
    ArrowUp,
    ArrowDown,
    Minus,
} from 'lucide-react';

// Kachelreihe mit den Rechnungs-Kennzahlen. Aufbau bewusst identisch zu
// QuoteKpiBar – Angebote und Rechnungen stehen im Cockpit nebeneinander und
// duerfen nicht wie zwei verschiedene Produkte aussehen.
//
// Rein praesentational: gerechnet wird in invoiceStats.ts, hier wird nur
// formatiert. Kein Date.now(), kein Zugriff auf Belege.
//
// Die Kacheln dieser Reihe sind bis auf eine STICHTAGSGROESSEN und gelten immer
// fuer heute. Sie aendern sich nicht, wenn der Nutzer den Zeitraum umstellt.
// Deshalb traegt jede Kachel einen Badge, der ihren Bezug nennt: "Stand heute"
// bei den Bestandsgroessen. Ohne diesen Hinweis liest sich eine Kachel wie eine
// Zeitraumgroesse, und der Nutzer haelt eine unveraenderte Forderungssumme nach
// dem Umschalten fuer einen Fehler.
//
// DIE AUSNAHME ist "Gestellt" — eine Flussgroesse ueber den laufenden
// Kalendermonat. Sie steht bewusst hier oben und nicht in der Fusszeile, weil
// die Frage "wie viel habe ich diesen Monat geschrieben" neben den offenen
// Forderungen gestellt wird. Ihr Badge nennt den Monat statt "Stand heute", und
// sie haengt NICHT am eingestellten Zeitraum — die Fusszeile tut das, und beides
// in einer Zeile waere nicht auseinanderzuhalten.

/**
 * Filter, den eine Kachel meldet. Union absichtlich deckungsgleich mit dem
 * lokalen Filtertyp der Rechnungsliste, damit der Aufrufer den Setter direkt
 * durchreichen kann.
 */
export type InvoiceKpiFilter = InvoiceStatus | 'all' | 'open';

/** Badge fuer Bestandsgroessen – gilt fuer jede Kachel dieser Reihe. */
const AS_OF_TODAY = 'Stand heute';

type Tone = 'blue' | 'amber' | 'slate' | 'emerald' | 'red';

const TONE: Record<Tone, { icon: string; value: string; ring: string }> = {
    blue: { icon: 'bg-blue-50 text-blue-600', value: 'text-slate-800', ring: 'hover:border-blue-300' },
    amber: { icon: 'bg-amber-50 text-amber-600', value: 'text-slate-800', ring: 'hover:border-amber-300' },
    slate: { icon: 'bg-slate-100 text-slate-500', value: 'text-slate-800', ring: 'hover:border-slate-300' },
    emerald: { icon: 'bg-emerald-50 text-emerald-600', value: 'text-slate-800', ring: 'hover:border-emerald-300' },
    red: { icon: 'bg-red-50 text-red-600', value: 'text-red-700', ring: 'hover:border-red-300' },
};

interface TileProps {
    caption: string;
    /** Kennzeichnet, worauf sich die Zahl bezieht – bei den Bestandsgroessen der Stichtag. */
    badge?: string;
    value: string;
    /**
     * Bezugsgroesse hinter dem Wert, z. B. "brutto". Steht an den Geldkacheln,
     * weil die Fusszeile daneben netto rechnet – ohne die Angabe liest sich die
     * Reihe wie eine einzige Bezugsgroesse.
     */
    unit?: string;
    sub: string;
    /**
     * Linkzeile unter dem Untertitel, nur bei klickbarer Kachel: sagt, was der
     * Klick oeffnet. Ohne sie ist eine klickbare Kachel nur am Hover zu erkennen.
     */
    link?: string;
    /** Steht neben dem Wert. Bleibt leer, wo es nichts zu vergleichen gibt. */
    delta?: React.ReactNode;
    /** Erklaerung beim Ueberfahren – fuer das, was in die Kachel nicht passt. */
    title?: string;
    icon: React.ReactNode;
    tone: Tone;
    onClick?: () => void;
}

const Tile: React.FC<TileProps> = ({ caption, badge, value, unit, sub, link, delta, title, icon, tone, onClick }) => {
    const t = TONE[tone];
    const cls = `text-left w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm transition-all ${
        onClick ? `cursor-pointer hover:shadow-md ${t.ring}` : ''
    }`;
    const body = (
        <>
            <div className="flex items-start justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 leading-tight">
                    {caption}
                    {badge && (
                        <span className="ml-1 font-medium normal-case tracking-normal text-slate-400">· {badge}</span>
                    )}
                </span>
                <span className={`shrink-0 p-1.5 rounded-lg ${t.icon}`}>{icon}</span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
                <p className={`text-xl font-black tabular-nums leading-none ${t.value}`}>{value}</p>
                {unit && <span className="text-[11px] font-medium text-slate-400">{unit}</span>}
                {delta}
            </div>
            {/* Zwei Zeilen statt Abschneiden: der Vormonatsbetrag stand sonst als
                "264.755,..." da. Die Kacheln einer Reihe strecken sich auf
                dieselbe Hoehe, eine zweite Zeile verschiebt also nichts. */}
            <p className="mt-1 text-[11px] text-slate-400 line-clamp-2">{sub}</p>
            {link && onClick && <p className="mt-1 text-[10px] font-bold text-emerald-600">{link}</p>}
        </>
    );

    return onClick ? (
        <button type="button" onClick={onClick} title={title} data-glass="kachel" className={cls}>
            {body}
        </button>
    ) : (
        <div data-glass="kachel" className={cls} title={title}>
            {body}
        </div>
    );
};

/**
 * Pfeil und Prozentabweichung neben einem Wert.
 *
 * Gerundet wird EINMAL, und das gerundete Ergebnis entscheidet auch ueber den
 * Pfeil: sonst zeigt eine Abweichung von 0,4 % einen Aufwaertspfeil neben der
 * Beschriftung "0 %", und die Kachel widerspricht sich selbst.
 *
 * Mehr gestellt ist gut, deshalb gruen nach oben. Rot nach unten ist hier kein
 * Fehlerzustand, sondern die uebliche Lesart einer Umsatzkennzahl.
 */
const Delta: React.FC<{ pct: number | null }> = ({ pct }) => {
    if (pct === null) return null;
    const rounded = Math.round(pct);
    const dir = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
    const Icon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : Minus;
    const color =
        dir === 'up' ? 'text-emerald-600' : dir === 'down' ? 'text-red-600' : 'text-slate-400';
    return (
        <span className={`flex items-center gap-0.5 text-xs font-bold tabular-nums ${color}`}>
            <Icon className="w-3.5 h-3.5" />
            {/* Betrag ohne Vorzeichen – die Richtung steht schon im Pfeil. */}
            {Math.abs(rounded)} %
        </span>
    );
};

/**
 * Ein Wert der Fusszeile. Beschriftung und Zahl bleiben als Paar zusammen — beim
 * Umbruch stand sonst die Einheit ohne ihre Zahl in der naechsten Zeile.
 */
const Metric: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
    <span className="flex items-baseline gap-1 whitespace-nowrap">
        <span className="text-slate-400">{label}</span>
        <span className="font-bold tabular-nums text-slate-700">{value}</span>
        {hint && <span className="text-slate-400">{hint}</span>}
    </span>
);

interface InvoiceKpiBarProps {
    stats: InvoiceStats;
    /**
     * Beschriftung des gewaehlten Zeitraums, z. B. "90 T". Betrifft NUR die
     * Fusszeile mit den Flussgroessen – die Kacheln selbst sind davon unberuehrt.
     */
    periodLabel?: string;
    /** Weglassen macht die Kacheln nicht klickbar. */
    onFilter?: (filter: InvoiceKpiFilter) => void;
    /**
     * Klick auf "Gestellt": klappt die Aufschluesselung je Monat, Woche und
     * Tag auf oder zu. Weglassen macht diese Kachel nicht klickbar.
     */
    onIssuedBreakdown?: () => void;
    /** Ob die Aufschluesselung gerade offen ist — bestimmt nur den Linktext. */
    issuedBreakdownOpen?: boolean;
    /**
     * Die Zahlungen sind noch unterwegs.
     *
     * Betrifft AUSSCHLIESSLICH den durchschnittlichen Zahlungseingang: er
     * leitet sich aus dem letzten Zahlungseingang je Beleg ab. Ohne Zahlungen
     * gilt kein Beleg als beglichen, dsoDays waere null und die Angabe fiele
     * stillschweigend weg — der Nutzer laese das als "keiner zahlt". Alle
     * uebrigen Werte dieser Leiste stehen als Summen auf der Belegzeile selbst
     * (paid_cents, credited_cents) und sind vom Nachladen unberuehrt.
     */
    paymentsPending?: boolean;
}

export const InvoiceKpiBar: React.FC<InvoiceKpiBarProps> = ({
    stats,
    periodLabel,
    onFilter,
    onIssuedBreakdown,
    issuedBreakdownOpen,
    paymentsPending,
}) => {
    const belege = (n: number) => `${n} ${n === 1 ? 'Beleg' : 'Belege'}`;

    // Vormonatsvergleich. Wert und Prozentangabe meinen den GEKAPPTEN Vormonat,
    // und solange es eine Prozentangabe gibt, nennt der Untertitel denselben
    // Zeitraum. Nur wenn die Kappung nichts uebrig laesst (Monatsanfang) und
    // damit auch keine Abweichung entsteht, nennt der Untertitel den ganzen
    // Vormonat — siehe monthComparisonSub. Der volle Vormonat steht sonst nur
    // im Titel: wer die Zahl mit seiner Buchhaltung abgleicht, sucht ihn, aber
    // er gehoert nicht neben eine Abweichung, die sich auf etwas anderes bezieht.
    const prevLabel = monthLabel(stats.previousMonth);
    const monthTitle =
        `Ausgestellte Forderungen im ${monthLabel(stats.currentMonth)}, brutto. ` +
        `Die Prozentzahl vergleicht mit dem ${prevLabel} vom 1. bis zum ${stats.comparedThroughDay}. – ` +
        `bis zum selben Tag des Monats, sonst fiele der Vergleich jeden Monatsanfang aus, nur weil der Monat noch läuft. ` +
        `Ganzer ${prevLabel}: ${formatCents(stats.prevMonthFullCents)}. ` +
        `Klick öffnet die Aufschlüsselung je Monat, Woche und Tag.`;
    const monthSub = monthComparisonSub(stats, formatCents);

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Tile
                    caption="Offene Forderungen"
                    badge={AS_OF_TODAY}
                    value={formatCents(stats.openCents)}
                    unit="brutto"
                    sub={stats.openCount === 0 ? 'nichts ausstehend' : `${belege(stats.openCount)} ausstehend`}
                    icon={<Euro className="w-4 h-4" />}
                    tone="blue"
                    onClick={onFilter ? () => onFilter('open') : undefined}
                />
                <Tile
                    caption="Davon überfällig"
                    badge={AS_OF_TODAY}
                    value={formatCents(stats.overdueCents)}
                    unit="brutto"
                    sub={stats.overdueCount === 0 ? 'nichts überfällig' : `${belege(stats.overdueCount)} · mahnen`}
                    icon={<AlertTriangle className="w-4 h-4" />}
                    tone={stats.overdueCents > 0 ? 'red' : 'slate'}
                    onClick={onFilter ? () => onFilter('OVERDUE') : undefined}
                />
                {/*
                 * Kein Statusfilter wie bei den Nachbarn: die Liste filtert nach
                 * Status, nicht nach Rechnungsdatum, und ein Statusfilter zeigte
                 * hier eine andere Menge als die Kachel nennt. Der Klick oeffnet
                 * stattdessen die Aufschluesselung je Monat, Woche und Tag —
                 * die Frage "und was war es im Juli?" stellt sich genau an
                 * dieser Kachel.
                 */}
                <Tile
                    caption="Gestellt"
                    badge={monthLabel(stats.currentMonth)}
                    value={formatCents(stats.monthIssuedCents)}
                    unit="brutto"
                    delta={<Delta pct={stats.monthDeltaPct} />}
                    sub={monthSub}
                    link={issuedBreakdownOpen ? 'Aufschlüsselung ausblenden' : 'Je Monat · Woche · Tag ▸'}
                    title={monthTitle}
                    icon={<CalendarClock className="w-4 h-4" />}
                    tone={stats.monthIssuedCount > 0 ? 'emerald' : 'slate'}
                    onClick={onIssuedBreakdown}
                />
                <Tile
                    caption="Entwürfe"
                    badge={AS_OF_TODAY}
                    value={String(stats.draftCount)}
                    sub={stats.draftCount === 0 ? 'keine offenen Entwürfe' : 'noch nicht ausgestellt'}
                    icon={<PenLine className="w-4 h-4" />}
                    tone="slate"
                    onClick={onFilter ? () => onFilter('DRAFT') : undefined}
                />
                <Tile
                    caption="In Prüfung"
                    badge={AS_OF_TODAY}
                    value={String(stats.inReviewCount)}
                    sub={stats.inReviewCount === 0 ? 'nichts zur Freigabe' : 'wartet auf Freigabe'}
                    icon={<ClipboardCheck className="w-4 h-4" />}
                    tone={stats.inReviewCount > 0 ? 'amber' : 'slate'}
                    onClick={onFilter ? () => onFilter('READY_FOR_REVIEW') : undefined}
                />
                {/*
                 * Guthaben ist bewusst nicht klickbar: es gibt keinen Status
                 * "Guthaben", eine Ueberzahlung kann auf jedem ausgestellten
                 * Beleg stehen. Jeder Statusfilter waere hier geraten und wuerde
                 * eine andere Menge zeigen als die Kachel nennt.
                 */}
                <Tile
                    caption="Kundenguthaben"
                    badge={AS_OF_TODAY}
                    value={formatCents(stats.customerCreditCents)}
                    unit="brutto"
                    sub={stats.customerCreditCents > 0 ? 'unverbrauchte Überzahlungen' : 'keine Überzahlungen'}
                    icon={<Wallet className="w-4 h-4" />}
                    tone={stats.customerCreditCents > 0 ? 'emerald' : 'slate'}
                />
            </div>

            {/*
             * Fusszeile mit den Flussgroessen. Getrennt von den Kacheln und mit
             * eigenem Zeitraum-Hinweis, damit die beiden Sorten Kennzahlen nicht
             * in einer Reihe verschwimmen. Eigene Flaeche statt blossem Text auf
             * dem Seitenhintergrund: als freistehende Zeile las sie sich wie ein
             * Rest, der unter den Kacheln hervorschaut.
             */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Zeitraum{periodLabel ? ` ${periodLabel}` : ''}
                </span>
                {stats.issuedCount === 0 ? (
                    <span className="text-slate-400">keine Belege ausgestellt</span>
                ) : (
                    <>
                        <Metric label="Umsatz" value={formatCents(stats.revenueNetCents)} hint="netto" />
                        <Metric label="vereinnahmt" value={formatCents(stats.collectedCents)} hint="brutto" />
                        {paymentsPending ? (
                            <span className="text-slate-400">Ø Zahlungseingang wird geladen …</span>
                        ) : (
                            stats.dsoDays !== null && (
                                <Metric
                                    label="Ø Zahlungseingang"
                                    value={`${Math.round(stats.dsoDays)} Tage`}
                                    hint="ab Rechnungsdatum"
                                />
                            )
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
